import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, User, Building2, ArrowUpDown, Plus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { StatusBadge, RolBadge } from '../CompanyBadges';
import { STATUS_OPTIONS, ROL_OPTIONS } from '../../config/companyFieldsConfig';
import { useUser } from '../../context/UserContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Filtros por sección (Estado, Rol, Ordenación)
 */
export const SectionFilters = ({ sortBy, setSortBy, statusFilter, setStatusFilter, rolFilter, setRolFilter }) => (
  <div className="flex flex-wrap gap-2 items-center" onClick={(e) => e.stopPropagation()}>
    <Select value={sortBy} onValueChange={setSortBy}>
      <SelectTrigger className="w-[140px] h-8 text-xs">
        <ArrowUpDown className="w-3 h-3 mr-1" />
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="name">Alfabético</SelectItem>
        <SelectItem value="owner">Responsable</SelectItem>
      </SelectContent>
    </Select>
    <Select value={statusFilter} onValueChange={setStatusFilter}>
      <SelectTrigger className="w-[130px] h-8 text-xs">
        <SelectValue placeholder="Estado" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">Todos Estados</SelectItem>
        {STATUS_OPTIONS.map((status) => (
          <SelectItem key={status} value={status}>{status}</SelectItem>
        ))}
      </SelectContent>
    </Select>
    <Select value={rolFilter} onValueChange={setRolFilter}>
      <SelectTrigger className="w-[120px] h-8 text-xs">
        <SelectValue placeholder="Rol" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">Todos Roles</SelectItem>
        {ROL_OPTIONS.map((rol) => (
          <SelectItem key={rol} value={rol}>{rol}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

/**
 * Tarjeta de empresa individual
 */
export const CompanyCard = ({ company, onUpdated }) => {
  const navigate = useNavigate();
  const { isEditor, currentUser, editorUsers } = useUser();
  const [owner, setOwner] = useState(company.owner || '__none__');

  useEffect(() => {
    setOwner(company.owner || '__none__');
  }, [company.owner]);

  const handleOwnerChange = async (val) => {
    setOwner(val);
    try {
      const newValue = val === '__none__' ? null : val;
      await axios.patch(
        `${API}/companies/${company.id}`,
        { owner: newValue },
        { headers: { 'X-User-Email': currentUser?.email } }
      );
      toast.success('Responsable actualizado');
      onUpdated?.();
    } catch (err) {
      console.error(err);
      toast.error('Error al actualizar responsable');
      setOwner(company.owner || '__none__');
    }
  };

  return (
    <Card
      className="cursor-pointer hover:shadow-md transition-shadow"
      onClick={() => navigate(`/companies/${company.id}`)}
      data-testid={`company-card-${company.id}`}
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-medium tracking-tight">{company.name}</CardTitle>
        <div className="flex flex-wrap gap-1.5 mt-2">
          <StatusBadge status={company.status || 'Sin estado'} />
          <RolBadge rol={company.rol} />
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2 text-sm">
          {company.sector && (
            <span className="text-muted-foreground text-xs">{company.sector}</span>
          )}
          {/* Quick-edit owner dropdown for editors */}
          {isEditor ? (
            <div
              className="flex items-center gap-2 pt-2 border-t"
              onClick={(e) => e.stopPropagation()}
              data-testid={`company-owner-block-${company.id}`}
            >
              <User className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
              <Select value={owner} onValueChange={handleOwnerChange}>
                <SelectTrigger
                  className="h-8 text-xs"
                  data-testid={`company-owner-select-${company.id}`}
                >
                  <SelectValue placeholder="Sin responsable" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin responsable</SelectItem>
                  {(editorUsers || []).map((u) => (
                    <SelectItem key={u.email} value={u.name}>
                      {u.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            company.owner && (
              <div className="flex items-center gap-1 text-muted-foreground text-xs">
                <User className="w-3 h-3" />
                <span>Resp: {company.owner}</span>
              </div>
            )
          )}
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Sección de empresas (Públicas o Privadas) completa
 */
export const CompanySection = ({
  typeKey,       // 'publica' | 'privada'
  typeGroup,     // Objeto con companies, groups, etc.
  isExpanded,
  onToggle,
  sortBy,
  setSortBy,
  statusFilter,
  setStatusFilter,
  rolFilter,
  setRolFilter,
  expandedGroups,
  onToggleGroup,
  onNewCompany,
  onUpdated,
  isEditor
}) => {
  const colorConfig = {
    privada: { bg: 'bg-purple-500', header: 'bg-purple-500 bg-opacity-10 hover:bg-opacity-20' },
    publica: { bg: 'bg-green-500', header: 'bg-green-500 bg-opacity-10 hover:bg-opacity-20' }
  };
  
  const labels = {
    privada: 'Privadas',
    publica: 'Públicas'
  };
  
  const buttonColorConfig = {
    privada: 'bg-purple-500 text-white hover:bg-purple-600',
    publica: 'bg-green-500 text-white hover:bg-green-600'
  };

  const config = colorConfig[typeKey];
  const buttonColors = buttonColorConfig[typeKey];
  const label = labels[typeKey];

  return (
    <div className="border rounded-xl overflow-hidden">
      {/* Header */}
      <div 
        className={`py-3 px-4 cursor-pointer transition-colors ${config.header}`}
        onClick={() => onToggle(typeKey)}
        data-testid={`company-type-${typeKey}`}
      >
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            {isExpanded 
              ? <ChevronDown className="w-5 h-5" />
              : <ChevronRight className="w-5 h-5" />
            }
            <div className={`w-3 h-3 rounded-full ${config.bg}`}></div>
            <h2 className="text-lg font-bold">{label}</h2>
            <Badge variant="outline" className="ml-1">
              {typeGroup?.companies.length || 0} empresa{typeGroup?.companies.length !== 1 ? 's' : ''}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <SectionFilters
              sortBy={sortBy}
              setSortBy={setSortBy}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              rolFilter={rolFilter}
              setRolFilter={setRolFilter}
            />
            {isEditor && (
              <Button 
                size="sm" 
                className={`h-8 shadow-sm ${buttonColors}`}
                onClick={(e) => { e.stopPropagation(); onNewCompany(); }}
                data-testid={`add-company-${typeKey}-btn`}
              >
                <Plus className="w-4 h-4 mr-1" />
                Nueva Empresa
              </Button>
            )}
          </div>
        </div>
      </div>
      
      {/* Content */}
      {isExpanded && (
        <div className="p-4 space-y-4 bg-muted/20">
          {typeGroup?.companies.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Building2 className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p>No hay empresas {label.toLowerCase()}</p>
            </div>
          ) : (
            typeGroup?.groups.map((group) => {
              const groupKey = `${typeKey}-${group.key}`;
              const isGroupExpanded = expandedGroups[groupKey] ?? false;
              
              return (
                <div key={groupKey}>
                  {/* Group Header */}
                  {group.label && (
                    <div 
                      className="py-2 px-3 border rounded-lg bg-background cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => onToggleGroup(groupKey)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {isGroupExpanded 
                            ? <ChevronDown className="w-4 h-4 text-muted-foreground" />
                            : <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          }
                          {sortBy === 'owner' && <User className="w-4 h-4 text-muted-foreground" />}
                          <span className="font-medium">{group.label}</span>
                          <span className="text-xs text-muted-foreground">({group.companies.length})</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Company Cards */}
                  {isGroupExpanded && (
                    <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 ${group.label ? 'mt-3 ml-6' : ''}`}>
                      {group.companies.map((company) => (
                        <CompanyCard key={company.id} company={company} onUpdated={onUpdated} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};
